import * as React from "react"

import { cn } from "@/lib/utils"

/* ROUND CW — dates are PICKED, never typed.
   Owner's rule, app-wide: anywhere a date can be chosen it must come from the
   calendar, not from free text. Rather than touch 56 call sites, the behaviour
   lives here, on the shared Input, so every present and future date field gets
   it automatically:
     · clicking (or focusing) the field opens the native calendar,
     · keystrokes that would edit the value are swallowed — arrows, Tab, Esc
       and the shortcuts still work, so keyboard users are not locked out,
     · the calendar icon fills the whole control, so the click target is the
       field itself rather than the small glyph at its right edge.
   `type="month"` gets the same treatment (it is a picker too). Pass
   `allowTyping` on an individual field to opt out. */
const Input = React.forwardRef(({ className, type, allowTyping, onKeyDown, onClick, ...props }, ref) => {
  const isPicker = (type === "date" || type === "month") && !allowTyping;

  const openPicker = (e) => {
    if (isPicker) {
      try { e.currentTarget.showPicker?.(); } catch { /* unsupported — native UI still opens */ }
    }
    onClick?.(e);
  };
  const guardKeys = (e) => {
    if (isPicker) {
      const allowed = ["Tab", "Escape", "Enter", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"];
      if (!allowed.includes(e.key) && !e.ctrlKey && !e.metaKey) e.preventDefault();
    }
    onKeyDown?.(e);
  };

  return (
    <input
      type={type}
      className={cn(
        "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        isPicker && "aampl-datefield cursor-pointer",
        className
      )}
      ref={ref}
      onClick={openPicker}
      onKeyDown={guardKeys}
      {...props} />
  );
})
Input.displayName = "Input"

export { Input }
